import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trash2, Search } from 'lucide-react';

import { productsApi } from '../../../api/products.js';
import { Input } from '../../../components/ui/Input.jsx';

/**
 * Pick catalogue products and quantities for a transfer or a load.
 * `items` is an array of { productId, name, sku, quantity, cost? }.
 * `withCost` shows a per-unit cost input (used by loads).
 */
export function ProductItemPicker({ items, onChange, withCost = false, storeId }) {
  const [q, setQ] = useState('');
  const [sku, setSku] = useState('');
  const active = q.trim().length >= 2 || sku.trim().length >= 1;
  const searchQuery = useQuery({
    queryKey: ['products', 'pick', q, sku, storeId],
    enabled: active,
    queryFn: () => productsApi.list({ q: q.trim() || undefined, sku: sku.trim() || undefined, storeId: storeId || undefined, limit: 8 })
  });
  const results = (searchQuery.data && searchQuery.data.items) || [];

  const add = (p) => {
    if (items.some((i) => i.productId === p._id)) return;
    onChange([...items, { productId: p._id, name: p.name, sku: p.sku, quantity: 1, ...(withCost ? { cost: '' } : {}) }]);
    setQ(''); setSku('');
  };
  const setField = (productId, field, value) => onChange(items.map((i) => (i.productId === productId ? { ...i, [field]: value } : i)));
  const remove = (productId) => onChange(items.filter((i) => i.productId !== productId));

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Input label="Aggiungi prodotto" placeholder="Cerca per nome (min 2 caratteri)" value={q} onChange={(e) => { setQ(e.target.value); setSku(''); }} />
          <Input label="SKU" placeholder="codice SKU" value={sku} onChange={(e) => { setSku(e.target.value); setQ(''); }} />
        </div>
        {active && results.length > 0 && (
          <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-surface-elevated shadow-elevated">
            {results.map((p) => (
              <button key={p._id} type="button" className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-surface-subtle" onClick={() => add(p)}>
                <span className="truncate text-ink">{p.name}</span>
                <span className="shrink-0 text-xs text-ink-faint"><Search className="mr-1 inline h-3 w-3" />{p.sku}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-ink-faint">Nessun prodotto aggiunto.</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-surface">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Prodotto</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Qtà</th>
                {withCost && <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Costo unit.</th>}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((i) => (
                <tr key={i.productId}>
                  <td className="px-3 py-2"><span className="text-ink">{i.name}</span> <span className="text-xs text-ink-faint">{i.sku}</span></td>
                  <td className="px-3 py-2">
                    <input type="number" min={1} value={i.quantity} onChange={(e) => setField(i.productId, 'quantity', e.target.value)} className="h-8 w-20 rounded-md border border-border bg-surface-elevated px-2 text-sm focus:outline-none" />
                  </td>
                  {withCost && (
                    <td className="px-3 py-2">
                      <input type="number" min={0} step="0.01" placeholder="-" value={i.cost} onChange={(e) => setField(i.productId, 'cost', e.target.value)} className="h-8 w-24 rounded-md border border-border bg-surface-elevated px-2 text-sm focus:outline-none" />
                    </td>
                  )}
                  <td className="px-3 py-2 text-right">
                    <button type="button" className="text-ink-muted hover:text-danger" onClick={() => remove(i.productId)}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
